import { prepareMediaUploadFile } from "./prepare-upload-file";

export interface UploadedAvatarMedia {
  id: string;
  kind: "photo" | "video";
  url: string;
  filename: string;
  mime: string;
  size: number;
  consentStatus: "pending" | "approved" | "revoked";
  status: "pending" | "ready";
  createdAt: string;
}

/** Upload approved avatar media through private R2, with a local HEIC conversion. */
export async function uploadAvatarMedia(
  originalFile: File,
  consentAccepted: boolean,
): Promise<UploadedAvatarMedia> {
  const file = await prepareMediaUploadFile(originalFile);
  const endpoint = "/api/avatars/uploads";
  const response = await fetch(endpoint, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      filename: file.name,
      mime: file.type,
      size: file.size,
      consentAccepted,
    }),
  });

  if (response.status === 400) {
    const form = new FormData();
    form.append("files", file);
    form.append("consentAccepted", String(consentAccepted));
    const fallback = await fetch(endpoint, { method: "POST", body: form });
    if (!fallback.ok) throw await uploadError(fallback);
    const records = (await fallback.json()) as UploadedAvatarMedia[];
    if (!records[0]) throw new Error("Upload completed without an avatar record");
    return records[0];
  }

  if (!response.ok) throw await uploadError(response);
  const prepared = (await response.json()) as {
    uploadUrl: string;
    item: UploadedAvatarMedia;
  };
  const put = await fetch(prepared.uploadUrl, {
    method: "PUT",
    headers: { "content-type": file.type },
    body: file,
  });
  if (!put.ok) {
    await fetch(`${endpoint}/${prepared.item.id}`, { method: "DELETE" }).catch(
      () => {},
    );
    throw new Error("Direct upload to media storage failed");
  }

  const completed = await fetch(endpoint, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ action: "complete", id: prepared.item.id }),
  });
  if (!completed.ok) {
    await fetch(`${endpoint}/${prepared.item.id}`, { method: "DELETE" }).catch(
      () => {},
    );
    throw await uploadError(completed, "Uploaded file failed security verification");
  }
  return (await completed.json()) as UploadedAvatarMedia;
}

async function uploadError(
  response: Response,
  fallback = "Upload failed",
): Promise<Error> {
  const body = (await response.json().catch(() => ({}))) as { error?: string };
  return new Error(body.error ?? fallback);
}
